import { VisibilityOffRounded, VisibilityRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import { useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { UpdateResetPassword } from '../../api/reset-password-external-users';
import { useAppDispatch } from '../../hook/useAppRudux';
import { useQueryStringText } from '../../hook/useQueryString';
import loadingSlice from '../Loading/loadingSlice';
import { UserContextType } from '../UserProfile/userProfileSlice';
import { updateResetPasswordAsync } from './resetPasswordSlice';

interface Password {
  newPassword: string;
  confirmNewPassword: string;
  showNewPassword: boolean;
  showConfirmNewPassword: boolean;
}

export default function ResetPassword() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [password, setPassword] = useState<Password>({
    newPassword: '',
    confirmNewPassword: '',
    showNewPassword: false,
    showConfirmNewPassword: false
  });
  const [loading, setLoading] = useState<boolean>(false);

  const showNewPasswordError =
    Boolean(password.newPassword) && password.newPassword.length < 8;

  const showConfirmPasswordError =
    Boolean(password.newPassword) &&
    Boolean(password.confirmNewPassword) &&
    password.newPassword !== password.confirmNewPassword;

  const disabled =
    !Boolean(password.newPassword) ||
    !Boolean(password.confirmNewPassword) ||
    showNewPasswordError ||
    showConfirmPasswordError;

  // Use the useQueryStringText hook to get the token from the query string
  const token = useQueryStringText({ query: 'token' });

  // If there is no token, redirect to the root of the project
  if (!token) return <Redirect to="/" />;

  // Decode the token using the jwtDecode library
  let user;
  let exp;
  try {
    // Attempt to decode the token using jwtDecode
    const data: UserContextType = jwtDecode(token);
    user = data.user;
    exp = data.exp;
  } catch (error) {
    if (error instanceof Error) {
      const index = error.message.indexOf(':');
      const message =
        index > 0 ? error.message.substring(0, index) : error.message;
      dispatch(
        loadingSlice.actions.error({
          message
        })
      );
    }
    return <Redirect to="/" />;
  }

  // If the decoded token does not contain a "user" property, redirect to the root of the project
  if (!user) return <Redirect to="/" />;

  function handleClickShowPassword(
    key: 'showNewPassword' | 'showConfirmNewPassword'
  ) {
    setPassword({ ...password, [key]: !password[key] });
  }

  function handleMouseDownPassword(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (token && !showNewPasswordError && !showConfirmPasswordError) {
        const changePassword: UpdateResetPassword = {
          password: password.newPassword,
          token
        };
        await dispatch(updateResetPasswordAsync(changePassword)).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: 'Password was changed successfully'
          })
        );
        setTimeout(() => {
          history.push('/');
        }, 1000);
      } else {
        throw new Error('Token is required');
      }
    } catch {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ mb: 10, mx: 'auto' }} maxWidth="sm">
      <Typography
        sx={{
          mt: { xs: 3.5, sm: 1.5 },
          mb: { xs: 4, sm: 2 },
          textAlign: { xs: 'center', sm: 'left' }
        }}
        component="p"
        variant="h4"
      >
        Reset Password
      </Typography>
      <Paper sx={{ p: 2, mb: 2, borderRadius: '16px' }}>
        <Typography
          sx={{ fontSize: 12, mb: 1.5 }}
          color="text.secondary"
          variant="body2"
        >
          {`You are changing the password of "${user.email.toLowerCase()}" account, please change it
          before ${
            exp && moment(exp * 1000).format('DD MMM yyyy, HH:mm')
          } to prevent link expired`}
        </Typography>
        <Alert sx={{ mb: 2 }} severity="info">
          Please create a new password that you don't use any other site
        </Alert>
        <form onSubmit={onSubmit}>
          <FormControl
            sx={{ m: '0.5rem 0', width: '100%' }}
            variant="outlined"
            size="small"
          >
            <InputLabel htmlFor="new-password" error={showNewPasswordError}>
              New Password
            </InputLabel>
            <OutlinedInput
              id="new-password"
              aria-describedby="new-password-error-text"
              type={password.showNewPassword ? 'text' : 'password'}
              value={password.newPassword}
              onChange={(event) =>
                setPassword({ ...password, newPassword: event.target.value })
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => handleClickShowPassword('showNewPassword')}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="large"
                  >
                    {password.showNewPassword ? (
                      <Tooltip title="Hide password">
                        <VisibilityOffRounded />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Show password">
                        <VisibilityRounded />
                      </Tooltip>
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="New Password"
              placeholder="Create new password..."
              error={showNewPasswordError}
            />
            {showNewPasswordError && (
              <FormHelperText
                id="new-password-error-text"
                error
                sx={{ lineHeight: '120%', mt: 0.75 }}
              >
                Be minimum of 8 characters
              </FormHelperText>
            )}
          </FormControl>
          <FormControl
            sx={{ m: '0.5rem 0', width: '100%' }}
            variant="outlined"
            size="small"
          >
            <InputLabel
              htmlFor="confirm-password"
              error={showConfirmPasswordError}
            >
              Confirm Password
            </InputLabel>
            <OutlinedInput
              id="confirm-password"
              aria-describedby="confirm-password-error-text"
              type={password.showConfirmNewPassword ? 'text' : 'password'}
              value={password.confirmNewPassword}
              onChange={(event) =>
                setPassword({
                  ...password,
                  confirmNewPassword: event.target.value
                })
              }
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() =>
                      handleClickShowPassword('showConfirmNewPassword')
                    }
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="large"
                  >
                    {password.showConfirmNewPassword ? (
                      <Tooltip title="Hide password">
                        <VisibilityOffRounded />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Show password">
                        <VisibilityRounded />
                      </Tooltip>
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm Password"
              placeholder="Confirm your password..."
              error={showConfirmPasswordError}
            />
            {showConfirmPasswordError && (
              <FormHelperText
                id="confirm-password-error-text"
                error
                sx={{ lineHeight: '120%', mt: 0.75 }}
              >
                Your new password and confirmation password must match
              </FormHelperText>
            )}
          </FormControl>
          <LoadingButton
            sx={{ mt: 3, mb: 1, fontSize: 16 }}
            type="submit"
            color="primary"
            variant="contained"
            size="large"
            disabled={disabled}
            loadingIndicator="Loading..."
            loading={loading}
            fullWidth
          >
            Change password
          </LoadingButton>
        </form>
      </Paper>
    </Box>
  );
}
